/**
 * waitlistService.js
 * Frontend service for UniLedge Waitlist API
 * 
 * Backend routes use PATH PARAMS for check and geo-check:
 *   POST /api/waitlist/signup          — body: { email, zip, name, phone, firebaseUid, referredBy, source }
 *   POST /api/waitlist/upgrade         — body: { signupId, phone, name, address, householdSize, budget, preferences }
 *   GET  /api/waitlist/stats           — public, no params
 *   GET  /api/waitlist/geo-check/:zip  — path param
 *   GET  /api/waitlist/me/:email       — path param
 *   GET  /api/waitlist/referral/:code  — path param
 *   GET  /api/waitlist/referral-stats/:code — path param
 *   GET  /api/waitlist/check/:email    — path param
 *
 * Place this file at: src/services/waitlistService.js
 * 
 * NOTE: REACT_APP_API_URL already includes /api (e.g. https://api.uniledge.io/api)
 */

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// ─── Helpers ────────────────────────────────────────────────────────

async function request(url, options = {}) {
  try {
    const res = await fetch(url, {
      headers: { 'Content-Type': 'application/json', ...options.headers },
      ...options,
    });
    const data = await res.json();
    if (!res.ok) {
      return { success: false, error: data.error || data.message || `HTTP ${res.status}`, status: res.status };
    }
    return { success: true, ...data };
  } catch (err) {
    console.error(`[waitlistService] ${url} failed:`, err);
    return { success: false, error: 'Network error — please try again.' };
  }
}

// ─── Public API ─────────────────────────────────────────────────────

/**
 * Get waitlist stats (total signups, Sacramento count, etc.)
 * Public — no auth required.
 */
export async function getStats() {
  return request(`${API_BASE_URL}/waitlist/stats`);
}

/**
 * Sign up for the waitlist.
 * @param {Object} data
 * @param {string} data.email         — required
 * @param {string} data.zip           — from geo-detect or manual entry
 * @param {string} [data.name]        — optional (full-profile tier)
 * @param {string} [data.phone]       — optional (phone tier)
 * @param {string} [data.firebaseUid] — if Google Auth was used
 * @param {string} [data.referredBy]  — referral code from URL param
 * @param {string} [data.source]      — traffic source: tiktok | instagram | referral | direct | etc.
 */
export async function signup(data) {
  return request(`${API_BASE_URL}/waitlist/signup`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

/**
 * Upgrade a waitlist signup to a higher tier (phone, full profile).
 * @param {string} signupId — the WaitlistSignup _id (may be undefined if ID wasn't returned)
 * @param {Object} data     — fields to add (phone, email, name, address, householdSize, budget, preferences)
 */
export async function upgradeTier(signupId, data) {
  // Use email as fallback identifier when signupId is unavailable
  const identifier = signupId || data.email;
  return request(`${API_BASE_URL}/waitlist/upgrade`, {
    method: 'POST',
    body: JSON.stringify({ signupId: identifier, ...data }),
  });
}

/**
 * Check whether a zip code falls inside the Sacramento launch zone.
 * Backend route: GET /api/waitlist/geo-check/:zip (PATH PARAM)
 * @param {string} zip — 5-digit US zip code
 * @returns {{ isInZone: boolean, city: string, state: string }}
 */
export async function geoCheck(zip) {
  return request(`${API_BASE_URL}/waitlist/geo-check/${encodeURIComponent(zip)}`);
}

/**
 * Look up the current user's waitlist record by email.
 * Backend route: GET /api/waitlist/me/:email (PATH PARAM)
 * @param {string} email
 */
export async function getMySignup(email) {
  return request(`${API_BASE_URL}/waitlist/me/${encodeURIComponent(email)}`);
}

/**
 * Get the user's unique referral code/link.
 * Backend route: GET /api/waitlist/referral/:code (PATH PARAM)
 * @param {string} signupId — used as referral code lookup
 */
export async function getReferralLink(signupId) {
  return request(`${API_BASE_URL}/waitlist/referral/${encodeURIComponent(signupId)}`);
}

/**
 * Get referral stats: how many friends joined, tokens earned.
 * Backend route: GET /api/waitlist/referral-stats/:code (PATH PARAM)
 * @param {string} signupId
 */
export async function getReferralStats(signupId) {
  return request(`${API_BASE_URL}/waitlist/referral-stats/${encodeURIComponent(signupId)}`);
}

/**
 * Quick check if an email is already on the waitlist.
 * Backend route: GET /api/waitlist/check/:email (PATH PARAM)
 * @param {string} email
 * @returns {{ exists: boolean, signupTier: string }}
 */
export async function checkExisting(email) {
  return request(`${API_BASE_URL}/waitlist/check/${encodeURIComponent(email)}`);
}

/**
 * Detect user location via IP (calls existing geo endpoint).
 * Returns city, state, coordinates, source.
 */
export async function detectLocation() {
  return request(`${API_BASE_URL}/geo/location`);
}